import _ from 'lodash'
import moment from 'moment-timezone'
import { connect } from 'react-redux'
import styled from 'styled-components'
import * as styles from '../assets/styles/eventOverview'
import { GUESTLIST_FREE_TYPE, GUESTLIST_TYPE, REQUEST_TYPE, RESERVATION_TYPE } from '../reducers/inventoryReducer'
import { isMultiInventoryEnabled } from '../selectors/eventSelector'
import Inventory from './Inventory'
import type { Map } from 'immutable'

const getNotAvailableMessage = (date: string) => {
  let msg = 'Booking for this event has not been opened yet, please check back at a later date.'
  if (date) {
    if (date.indexOf('T') !== -1) {
      const openDate = moment(date).format('MMMM D, YYYY')
      msg = `Booking for this event opens on ${openDate}.`
    }
  }
  return msg
}

interface InventoryAreaProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  inventoryData: { [key: string]: any }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  inventoryEdits: { [inventoryId: string]: any }
  isPrivate: boolean
  isBookable: boolean
  isEventOpened: boolean
  date: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tieredEvents: any[] | null
  isExperienceMode: boolean
  onSvgLayerClick: (inventoryName: string, selectedDate: string) => void
  isMultiInventoryEnabled: boolean
  hasAtLeastOneItemInCart: boolean
}

const InventoryList = ({
  inventoryData,
  inventoryEdits,
  isPrivate,
  isBookable,
  isEventOpened,
  date,
  tieredEvents,
  isExperienceMode,
  onSvgLayerClick,
  isMultiInventoryEnabled,
  hasAtLeastOneItemInCart,
}: InventoryAreaProps) => {
  const isNotAvailable = !isEventOpened && _.isEmpty(inventoryData)

  if (isNotAvailable) {
    const notAvailableMessage = getNotAvailableMessage(date)
    return <AlertMessage message={notAvailableMessage} />
  }

  const isSoldOut = _.isEmpty(inventoryData) || (!isExperienceMode && isPrivate && !isBookable)

  if (isSoldOut) {
    return <AlertMessage message="This event is sold out." />
  }

  if (isMultiInventoryEnabled) {
    const shouldDisplayInventory = (inventoryItem: { inventoryId: string; inventoryType: string; maxQuantity: number }) => {
      if (Array.isArray(tieredEvents) && tieredEvents.indexOf(inventoryItem.inventoryId) >= 0) {
        return false
      }

      const { inventoryId, inventoryType } = inventoryItem
      const inventoryAvailability = inventoryEdits && inventoryEdits[inventoryId]
      const remainingQuantity = Math.min(
        inventoryItem.maxQuantity,
        inventoryAvailability ? inventoryAvailability.remainingQuantity : inventoryItem.maxQuantity
      )

      if (remainingQuantity <= 0 && inventoryType !== REQUEST_TYPE) {
        return false
      }

      return true
    }

    const inventoryDataList = Object.values(inventoryData)
      .filter(inventory => shouldDisplayInventory(inventory))
      .sort((inventoryA, inventoryB) => inventoryA.viewOrder - inventoryB.viewOrder)

    const inventoryTypes = new Set<string>()
    inventoryDataList.forEach((inventory: { inventoryId: string; inventoryType: string; maxQuantity: number }) => {
      if (inventory.inventoryType === GUESTLIST_TYPE || inventory.inventoryType === GUESTLIST_FREE_TYPE) {
        inventoryTypes.add(GUESTLIST_TYPE)
      } else {
        inventoryTypes.add(inventory.inventoryType)
      }
    })
    const inventoryTypeDisplayOrder = Array.from(inventoryTypes)

    return (
      <div>
        {inventoryTypeDisplayOrder.map(inventoryType => {
          const currentTypeInventoryData = inventoryDataList.filter(inventory => {
            if (inventoryType === RESERVATION_TYPE || inventoryType === REQUEST_TYPE) {
              return inventory.inventoryType === inventoryType
            }

            return inventory.inventoryType === GUESTLIST_TYPE || inventory.inventoryType === GUESTLIST_FREE_TYPE
          })

          if (currentTypeInventoryData.length === 0) {
            return null
          }

          const selectedInventoryForCurrentType = currentTypeInventoryData.filter(({ inventoryId }) => {
            const inventoryAvailability = inventoryEdits && inventoryEdits[inventoryId]
            return inventoryAvailability && inventoryAvailability.quantity > 0
          })
          const isDisabled = selectedInventoryForCurrentType.length === 0 && hasAtLeastOneItemInCart

          let headerTitle = 'Guestlist'
          if (inventoryType === RESERVATION_TYPE) {
            headerTitle = 'Reservations'
          } else if (inventoryType === REQUEST_TYPE) {
            headerTitle = 'Requests'
          }

          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const rgbaLineBackgroundColor = styles.rgbaLineBackground!

          return (
            <section key={inventoryType} style={{ backgroundColor: rgbaLineBackgroundColor, color: fontsColorPrimary }}>
              <>
                <hr style={styles.inventoryItemSeperator} />
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <h2 style={{ fontSize: '18px', fontWeight: 400, margin: 0, padding: '16px' }}>{headerTitle}</h2>
                </div>
                <hr style={styles.inventoryItemSeperator} />
              </>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {currentTypeInventoryData.map(inventoryItem => (
                  <li key={inventoryItem.id} style={{ borderBottom: `solid 1px ${rgbaLineBackgroundColor}` }}>
                    <Inventory
                      inventoryItem={inventoryItem}
                      inventoryEdits={inventoryEdits}
                      isDisabled={isDisabled}
                      onSvgLayerClick={onSvgLayerClick}
                    />
                  </li>
                ))}
              </ul>
            </section>
          )
        })}
      </div>
    )
  }

  const sortedInventory = _.sortBy(inventoryData, 'viewOrder')
    .filter(inventoryItem => !_.isArray(tieredEvents) || tieredEvents.indexOf(inventoryItem.inventoryId) === -1)
    .filter(inventoryItem => {
      const { inventoryId, inventoryType } = inventoryItem
      const inventoryEdit = inventoryEdits && inventoryEdits[inventoryId]
      const remainingQuantity = Math.min(
        inventoryItem.maxQuantity,
        inventoryEdit ? inventoryEdit.remainingQuantity : inventoryItem.maxQuantity
      )

      return remainingQuantity > 0 || inventoryType === 'REQUEST'
    })

  return (
    <div>
      {sortedInventory.map(inventoryItem => (
        <div key={inventoryItem.id}>
          <Inventory inventoryItem={inventoryItem} inventoryEdits={inventoryEdits} onSvgLayerClick={onSvgLayerClick} />
          <StyledHr />
        </div>
      ))}
    </div>
  )
}

interface State {
  userSelection: Map<string, string>
  queryObj: {
    eventId: string
    experienceId: string
  }
  inventoryCart: {
    cart: {
      quantity: number
    }[]
  }
}

const mapStateToProps = (state: State) => {
  const {
    userSelection,
    queryObj: { eventId, experienceId },
  } = state

  return {
    isBookable: userSelection.get('eventId') === eventId,
    isExperienceMode: !!experienceId,
    hasAtLeastOneItemInCart: state.inventoryCart.cart.filter(({ quantity }) => quantity > 0).length > 0,
    isMultiInventoryEnabled: isMultiInventoryEnabled(state),
  }
}

export default connect(mapStateToProps)(InventoryList)

const AlertMessage = ({ message }: { message: string }) => <StyledAlertMessageContainer>{message}</StyledAlertMessageContainer>

const { fonts_color_secondary: fontsColorSecondary, fonts_color_primary: fontsColorPrimary } = window.widgetInit.settings
const StyledAlertMessageContainer = styled.div`
  background-color: ${styles.rgbaCellBackground};
  padding: 20px;
  font-size: 18px;
  font-style: italic;
  color: ${fontsColorSecondary};
`

const StyledHr = styled.hr`
  border: 1px solid ${styles.rgbaLineBackground};
  margin: 0;
`
